<template>
  <div
    class="styled-with"
    :class="{loadingClass: !ready}"
  >
    <div class="styled-with__art">
      <art-direction
        block-class="styled-with"
        breakpoint-sizes="260-260-260-260-260-260"
        :image="activeProduct.images[0]"
        :image-desktop="activeProduct.images[0]"
        ratio="260:474"
        ratio-desktop="260:456"
      />
    </div>

    <div class="styled-with__details">
      <h3
        class="styled-with__title"
        v-text="activeProduct.title"
      />

      <product-prices
        :available="activeProduct?.available"
        block-class="styled-with"
        :compare-at-price="activeProduct?.compareAtPrice?.amount"
        :price="activeProduct?.price?.amount"
        :price-first="false"
        element="div"
      />

      <main-product-swatches
        v-if="swatches.length"
        :product="activeProduct"
        :swatches="swatches"
        :show-all="false"
        @swatch-updated="handleSwatchUpdate"
        @swatch-loaded="swatchLoaded"
      />

      <product-form
        :product="product"
        :variant="variant"
        :show-header="false"
        :shop-the-look="false"
        @update-variant="handleVariantUpdate"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Styled with (styled-with)
 * -----------------------------------------------------------------------------
 * Display recommended paired products on PDP.
 * @param {Object} product
 *
 */
import classes from '~/config/classes'
import productsWithMetafields from '~/graphql/queries/productsWithMetafields.gql'
import { formatProduct } from '~/helpers/format'
import { handleize } from '~/helpers/general'
import MainProductSwatches from '~async/main-product/main-product-swatches/main-product-swatches'
import ProductForm from '~async/product-form/product-form'
import ArtDirection from '~global/images/art-direction'
import ProductPrices from '~global/product-prices/product-prices'

export default {
  name: 'StyledWith',

  components: {
    ArtDirection,
    ProductPrices,
    MainProductSwatches,
    ProductForm,
  },

  props: {
    product: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      ready: false,
      swatchProducts: [],
      swatches: [],
      activeProduct: this.product,
      variant: false,
    }
  },

  computed: {

    /**
     * Compute product master title.
     */
    masterTitle() {
      return this.product.title.split(' - ')[0].trim()
    },

    /**
     * Compute loading class
     */
    loadingClass() {
      return classes.loading
    },
  },

  watch: {

    /**
     * Watch for updated swatch products.
     */
    swatchProducts() {
      this.getSwatchData()
    },
  },

  mounted() {
    this.querySwatchProducts()
  },

  methods: {

    /**
     * load products from GraphQL and sort currently active product to front
     */
    async querySwatchProducts() {
      let query = `(title:'${this.masterTitle}'*)`

      if (this.gender) {
        query += ` AND (tag:'${this.gender}')`
      }

      try {
        const response = await cnvs.Query({
          locale: cnvs.store.language,
          query: productsWithMetafields,
          variables: {
            country: cnvs.store.country,
            query,
            namespace: 'descriptors',
            modelSizeCaptionKey: 'model_size_caption',
            preorderShippingDateKey: 'preorder_shipping_date',
            washKey: 'machine_wash',
            fabricKey: 'fabric',
            importedKey: 'imported',
            careGuideKey: 'care_guide_content',
            sizeFitKey: 'size_fit',
            featuresDetailsKey: 'features_details',
            styledWithKey: 'styled_with',
            sizeGuideKey: 'size_guide',
          },
        })

        if (!response.products.edges.length) {
          return
        }

        const tmpSwatches = []

        response.products.edges.forEach((product) => {
          const data = { product: product.node }
          if (this.isProductMatch(data.product.title)) {
            tmpSwatches.push(formatProduct(data))
          }
        })

        const activeProduct = tmpSwatches.find((product) => {
          return this.product.id === product.id
        })

        const index = tmpSwatches.indexOf(activeProduct)
        tmpSwatches.splice(index, 1)
        tmpSwatches.unshift(activeProduct)

        this.swatchProducts = tmpSwatches
        this.ready = true

      } catch (error) {
        reportError('Failed to load product', error)
      }
    },

    /**
     * Determine if queried product matches current product
     * @param {String}
     * @returns {Boolean}
     */
    isProductMatch(title) {
      const masterTitle = this.masterTitle.toLowerCase()
      const newTitle = title.toLowerCase().split(' - ')[0].trim()

      return masterTitle === newTitle
    },

    /**
     * Get list of all relevant swatch images
     * sorted by Product ID.
     */
    getSwatchData() {
      this.swatches = this.swatchProducts.map((product) => {
        const color = product.options.find((option) => {
          return option.name.toLowerCase() === 'color'
        })

        if (!color) {
          return false
        }

        const location = window.cnvs.urls.files.split('placeholder')[0]
        const key = handleize(color.values[0])

        const fabricTag = product.tags.find((tag) => {
          const clean = tag.toLowerCase()

          return clean.includes('fabric')
        })

        let swatchSrc = `${location}color`

        if (fabricTag) {
          const fabric = fabricTag.split(':')[1]
            .split(' ')
            .join('-')
            .toLowerCase()
            .trim()

          swatchSrc = `${swatchSrc}-${fabric}`
        }

        swatchSrc = `${swatchSrc}-${key}_32x10.png`

        const isSale =
          Boolean(product.compareAtPrice.amount &&
            product.compareAtPrice.amount > product.price.amount)

        return {
          available: product.available,
          id: product.id,
          image: swatchSrc,
          name: color.values[0],
          sale: isSale,
        }
      })
    },

    /**
     * Update section with data from newly selected swatch.
     * @param {Object} swatch - Selected Swatch.
     */
    handleSwatchUpdate(swatch) {
      const product = this.swatchProducts.find((data) => {
        return data.id === swatch.id
      })

      if (!product) {
        return
      }

      this.activeProduct = product
    },

    /**
     * Add failed attribute to swatch images that 404
     * @param {Object} swatch
     */
    swatchLoaded(swatch) {
      swatch.loaded = true
    },

    /**
     * Handle variant update from product form.
     * @param {Object} variant - Selected variant.
     */
    handleVariantUpdate(variant) {
      this.variant = variant
    },
  },
}
</script>

<style lang="scss">
@import './styled-with';
</style>
